import {
  Box,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ToggleButton,
} from "@mui/material";
import { OrionToggleButtonGroup } from "@orion/OrionComponents/OrionToggleButtonGroup";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useCallback, useState, useEffect, useMemo } from "react";
import { z } from "zod";

import type { ChannelMedium, OrderBy } from "@/types/dragon-tiles";
import type { SelectChangeEvent } from "@mui/material";

import { useDragonTilesLite } from "@/api/dragon-tiles";
import { LoadingFallback } from "@/components/LoadingFallback";
import { PreviewDrawer } from "@/components/PreviewDrawer";
import {
  isByocDragonTile,
  useDragonTileDisplayName,
} from "@/features/Connectors/byocTileDisplayName";
import { useConnectorsByDragonTileId } from "@/features/Connectors/hooks/useConnectorsByDragonTileId";
import { CreateFrameModal } from "@/features/DragonTiles/components/CreateFrameModal";
import {
  AddIcon,
  FormatListBulletedIcon,
  GridViewIcon,
  SearchIcon,
} from "@/icons";
import { CHANNEL_MEDIUMS, UNASSIGNED_DRAGON_TILE } from "@/types/dragon-tiles";

/** ================== URL Search State (typed) ================== */
export type CreativeSphereSearch = {
  search?: string; // search by name
  create?: boolean; // open Create modal
  medium?: ChannelMedium; // filter by medium
  dragon_tile?: string; // filter by dragon tile ID
  event?: string; // filter by connector event ID (only with dragon_tile)
  order_by?: OrderBy; // order by field
  order?: "asc" | "desc"; // order direction
  view?: "grid" | "table"; // view mode
  preview?: string; // frameId to preview in drawer
  deactivated?: boolean; // show permanently deactivated frames
  is_active?: boolean; // filter by active status
  expectedRPM?: boolean; // show expected RPM benchmarks
};

export const SearchSchema = z
  .object({
    search: z.string().optional(),
    create: z
      .union([z.string(), z.boolean()])
      .transform((v) => (typeof v === "string" ? v === "true" : !!v))
      .optional(),
    medium: z.enum(CHANNEL_MEDIUMS).optional(),
    dragon_tile: z.string().optional(),
    event: z.string().optional(),
    order_by: z.enum(["create_date", "modify_date", "name"]).optional(),
    order: z.enum(["asc", "desc"]).optional(),
    view: z.enum(["grid", "table"]).optional(),
    preview: z.string().optional(),
    deactivated: z
      .union([z.string(), z.boolean()])
      .transform((v) => (typeof v === "string" ? v === "true" : !!v))
      .optional(),
    is_active: z
      .union([z.string(), z.boolean()])
      .transform((v) => (typeof v === "string" ? v === "true" : v))
      .optional(),
    expectedRPM: z
      .union([z.string(), z.boolean()])
      .transform((v) => (typeof v === "string" ? v !== "false" : !!v))
      .optional(),
  })
  .transform(
    (s): CreativeSphereSearch => ({
      search: s.search,
      create: s.create,
      medium: s.medium,
      dragon_tile: s.dragon_tile,
      // An event only narrows a connector's tiles, so it never stands alone.
      event: s.dragon_tile ? s.event : undefined,
      order_by: s.order_by,
      order: s.order,
      view: s.view,
      preview: s.preview,
      deactivated: s.deactivated,
      is_active: s.is_active,
      expectedRPM: s.expectedRPM,
    }),
  );

/** ================== Route ================== */
export const Route = createFileRoute("/_auth/creative-sphere/$siteId")({
  validateSearch: (raw): CreativeSphereSearch => SearchSchema.parse(raw),
  component: RouteComponent,
  pendingComponent: LoadingFallback,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const createOpen = !!search.create;
  const previewFrameId = search.preview;

  const closePreview = useCallback(() => {
    void navigate({
      search: (prev): CreativeSphereSearch => ({
        ...prev,
        preview: undefined,
      }),
      replace: true,
    });
  }, [navigate]);

  // Local state for search input with debouncing
  const [searchInput, setSearchInput] = useState(search.search ?? "");

  // Sync local search input with URL search param
  useEffect(() => {
    setSearchInput(search.search ?? "");
  }, [search.search]);

  // Debounce search input - update URL after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (search.search ?? "")) {
        void navigate({
          search: (prev): CreativeSphereSearch => ({
            ...prev,
            search: searchInput || undefined,
          }),
          replace: true,
        });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput, navigate, search.search]);

  // Helper to update a single search param
  const updateSearchParam = useCallback(
    <K extends keyof CreativeSphereSearch>(
      key: K,
      value: CreativeSphereSearch[K] | undefined,
    ) => {
      void navigate({
        search: (prev): CreativeSphereSearch => {
          const updated = { ...prev };
          if (value === undefined || value === "") {
            delete updated[key];
          } else {
            updated[key] = value;
          }
          return updated;
        },
        replace: true,
      });
    },
    [navigate],
  );

  const { data: dragonTiles } = useDragonTilesLite(siteId, "all");
  const getDisplayName = useDragonTileDisplayName(siteId);

  const dragonTileOptions = useMemo(() => {
    if (!dragonTiles) return [];
    return dragonTiles
      .map((tile) => {
        const name = getDisplayName(tile);
        return {
          id: tile.id,
          // Capitalization only tidies up the API's tile names; a site name
          // stands in for BYOC and is shown exactly as the site spells it.
          label: isByocDragonTile(tile)
            ? name
            : name.charAt(0).toUpperCase() + name.slice(1),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [dragonTiles, getDisplayName]);

  const { connectorByDragonTileId } = useConnectorsByDragonTileId(siteId);
  const eventOptions = useMemo(() => {
    const events =
      (search.dragon_tile &&
        connectorByDragonTileId.get(search.dragon_tile)?.installed.events) ||
      [];
    const options = events.map((event) => ({
      id: event.id,
      label: event.name,
    }));
    // Keep an event from the URL that the connector no longer offers (e.g. an
    // old shared link) visible, so the filter it applies can still be cleared.
    if (search.event && !options.some((option) => option.id === search.event)) {
      options.push({ id: search.event, label: search.event });
    }
    return options;
  }, [connectorByDragonTileId, search.dragon_tile, search.event]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: (theme) =>
          theme.palette.background["paper-elevation-1"],
      }}
    >
      {/* Header actions */}
      <Box
        sx={{
          p: 4,
          display: "flex",
          alignItems: "center",
          gap: 3,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {/* Search field */}
        <TextField
          size="small"
          placeholder="Search tiles by name"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            endAdornment: (
              <SearchIcon
                sx={{ mr: 2, color: "text.secondary" }}
                fontSize="small"
              />
            ),
          }}
          sx={{ width: 280 }}
        />

        {/* View Toggle */}
        <OrionToggleButtonGroup
          value={search.view ?? "grid"}
          exclusive
          onChange={(_, newView: "grid" | "table" | null) => {
            if (newView !== null) {
              updateSearchParam("view", newView);
            }
          }}
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridViewIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="table" aria-label="table view">
            <FormatListBulletedIcon fontSize="small" />
          </ToggleButton>
        </OrionToggleButtonGroup>

        {/* Medium filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={search.medium ?? ""}
            displayEmpty
            onChange={(e: SelectChangeEvent<string>) =>
              updateSearchParam(
                "medium",
                (e.target.value as ChannelMedium) || undefined,
              )
            }
          >
            <MenuItem value="">All Mediums</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">Text</MenuItem>
          </Select>
        </FormControl>

        {/* Dragon Tile filter */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Select
            data-testid="dragon-tile-filter"
            value={search.dragon_tile ?? ""}
            displayEmpty
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value;
              void navigate({
                search: (prev): CreativeSphereSearch => {
                  const updated = { ...prev };
                  // Events belong to the previous connector tile.
                  delete updated.event;
                  if (v) updated.dragon_tile = v;
                  else delete updated.dragon_tile;
                  return updated;
                },
                replace: true,
              });
            }}
          >
            <MenuItem value="">All Dragon Tiles</MenuItem>
            {dragonTileOptions.map((tile) => (
              <MenuItem key={tile.id} value={tile.id}>
                {tile.label}
              </MenuItem>
            ))}
            <MenuItem value={UNASSIGNED_DRAGON_TILE}>Unassigned</MenuItem>
          </Select>
        </FormControl>

        {/* Event filter (connector Dragon Tiles only) */}
        {eventOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              data-testid="event-filter"
              value={search.event ?? ""}
              displayEmpty
              onChange={(e: SelectChangeEvent<string>) =>
                updateSearchParam("event", e.target.value || undefined)
              }
            >
              <MenuItem value="">All Events</MenuItem>
              {eventOptions.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Active Status filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            data-testid="active-status-filter"
            value={
              search.deactivated
                ? "deactivated"
                : search.is_active === undefined
                  ? ""
                  : search.is_active
                    ? "active"
                    : "inactive"
            }
            displayEmpty
            onChange={(e: SelectChangeEvent<string>) => {
              const v = e.target.value;
              void navigate({
                search: (prev): CreativeSphereSearch => {
                  const updated = { ...prev };
                  delete updated.is_active;
                  delete updated.deactivated;
                  if (v === "active") updated.is_active = true;
                  else if (v === "inactive") updated.is_active = false;
                  else if (v === "deactivated") updated.deactivated = true;
                  return updated;
                },
                replace: true,
              });
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="deactivated">Permanently Deactivated</MenuItem>
          </Select>
        </FormControl>

        {/* Order By */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="order-by-label">Order By</InputLabel>
          <Select<OrderBy | "">
            data-testid="order-by-select"
            labelId="order-by-label"
            value={search.order_by ?? ""}
            label="Order By"
            onChange={(e) => {
              const value = e.target.value;
              if (value === "") {
                updateSearchParam("order_by", undefined);
                updateSearchParam("order", undefined);
                return;
              }
              updateSearchParam("order_by", value);
              if (!search.order) {
                updateSearchParam("order", "desc");
              }
            }}
          >
            <MenuItem value="">Default</MenuItem>
            <MenuItem value="modify_date">Modify Date</MenuItem>
            <MenuItem value="create_date">Create Date</MenuItem>
            <MenuItem value="name">Name</MenuItem>
          </Select>
        </FormControl>

        {/* Order direction (only show if order_by is set) */}
        {search.order_by && (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Order</InputLabel>
            <Select
              value={search.order ?? "desc"}
              label="Order"
              onChange={(e: SelectChangeEvent<string>) =>
                updateSearchParam(
                  "order",
                  (e.target.value as "asc" | "desc") || undefined,
                )
              }
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            void navigate({
              search: (prev): CreativeSphereSearch => ({
                ...prev,
                create: true,
              }),
              replace: false,
            })
          }
        >
          Create New Tile
        </Button>
      </Box>

      {/* Create Tile Modal (kept in URL as ?create=true for shareability) */}
      {createOpen && (
        <CreateFrameModal
          open={createOpen}
          onClose={() =>
            void navigate({
              search: (prev): CreativeSphereSearch => ({
                ...prev,
                create: false,
              }),
              replace: true,
            })
          }
          siteId={siteId}
          showParentTileField={true}
          returnTo="creative-sphere"
          creativeSphereParams={{
            medium: search.medium,
            dragon_tile: search.dragon_tile,
            event: search.event,
            order_by: search.order_by,
            order: search.order,
            search: search.search,
            view: search.view,
          }}
        />
      )}

      {/* Child route outlet - tile cards will render here */}
      <Outlet />

      {/* Preview Drawer */}
      <PreviewDrawer
        frameId={previewFrameId}
        siteId={siteId}
        onClose={closePreview}
      />
    </Box>
  );
}
